import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Grid, Chip, Button, Stack, Alert, TextField } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { ME_QUERY, SET_CONTACT_PHONE_MUTATION } from '../../graphql/queries';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../context/FamilyContext';
import { useCanOpen } from '../../context/PageAccessContext';

import {
  DashboardHeader, StatTile, KpiRow, Section, BarList, RowList, AttentionList, fmtDateTime, fmtDate, LinkButton,
} from './DashboardParts';

// Client pages to open after choosing a client, in order of preference
const CLIENT_PAGES = [
  '/dashboard/my-vendors', '/dashboard/guests', '/dashboard/service-providers', '/dashboard/catering',
  '/dashboard/venues', '/dashboard/rituals', '/dashboard/budget', '/dashboard/timeline',
];

const WISHLIST_STATUS = {
  draft: 'Draft', pending_confirmation: 'Awaiting confirmation', pending_approval: 'Awaiting admin approval',
  approved: 'Approved', rejected: 'Not approved', contracted: 'Contract created',
};

// The number families they manage — and those families' vendors — see to reach them
function ContactPhone() {
  const { data } = useQuery(ME_QUERY);
  const saved = data?.me?.phone || '';
  const [phone, setPhone] = useState(null); // null: not editing
  const [error, setError] = useState('');
  const [save, { loading }] = useMutation(SET_CONTACT_PHONE_MUTATION, {
    onCompleted: () => { setPhone(null); setError(''); },
    onError: (e) => setError(e.message),
    refetchQueries: [{ query: ME_QUERY }],
  });
  return (
    <Alert severity={saved ? 'info' : 'warning'} icon={<SupportAgentIcon />} sx={{ mb: 2 }}
      action={phone === null && <Button color="inherit" size="small" onClick={() => setPhone(saved)}>{saved ? 'Change' : 'Add number'}</Button>}>
      {phone === null ? (
        saved
          ? <>Families you manage, and their vendors, reach you on <strong>{saved}</strong>.</>
          : 'Add your contact number — the families you manage and their vendors see it as their point of contact.'
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField size="small" label="Contact number" value={phone} onChange={e => setPhone(e.target.value)} error={Boolean(error)} helperText={error || ' '} />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={loading} onClick={() => save({ variables: { phone } })}>Save</Button>
            <Button size="small" onClick={() => { setPhone(null); setError(''); }}>Cancel</Button>
          </Stack>
        </Stack>
      )}
    </Alert>
  );
}

export default function MatchmakerDashboard({ data }) {
  const navigate = useNavigate();
  const { family, setFamily } = useFamily();
  const canOpen = useCanOpen();
  const k = data.kpis;
  // Select the client as the family being managed, then open the first client page in the
  // matchmaker's own menu (the menu itself stays the matchmaker's)
  const manage = (c) => {
    setFamily({ id: c.id, email: c.email, displayName: c.name, roleName: c.role, roleDisplayName: c.roleLabel, serviceCategories: [] });
    const target = CLIENT_PAGES.find(p => canOpen(p));
    if (target) navigate(target);
  };
  const attention = [
    k.unreadMessages ? { text: `${k.unreadMessages} unread message(s)`, link: '/dashboard/messages' } : null,
    k.familiesAwaitingApproval ? { text: `${k.familiesAwaitingApproval} family wishlist(s) awaiting admin approval`, link: '/dashboard/wishlist-wizard', waiting: true } : null,
    k.newProfiles30d ? { text: `${k.newProfiles30d} new profile(s) in the last 30 days — review for matches`, link: '/dashboard/matchmaking' } : null,
  ].filter(Boolean);

  return (
    <>
      <DashboardHeader title="Matchmaker dashboard" subtitle="Profiles to match, your meetings and families you support" />
      <ContactPhone />
      <KpiRow>
        <StatTile hero label="My clients" value={k.myClients} sub={`${k.clientWishlistsPending} wishlist(s) in progress`} to="/dashboard/wishlist-wizard" />
        <StatTile label="Profiles to match" value={k.profiles} sub={`${k.brides} bride · ${k.grooms} groom`} to="/dashboard/matchmaking" />
        <StatTile label="New profiles" value={k.newProfiles30d} sub="last 30 days" to="/dashboard/matchmaking" />
        <StatTile label="Upcoming meetings" value={k.upcomingMeetings} sub={`${k.liveVendors} vendors available`} to="/dashboard/meetings" />
      </KpiRow>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Section title="My clients" action={<LinkButton to="/dashboard/wishlist-wizard">Service Wizard</LinkButton>}>
            <RowList
              rows={data.clients.map(c => ({
                primary: `${c.name}${c.city ? ` · ${c.city}` : ''}`,
                secondary: c.wishlists.map(w => `${WISHLIST_STATUS[w.status] || w.status} · ${w.services} service categories${w.eventDate ? ` · event ${fmtDate(w.eventDate)}` : ''}`).join('  |  ')
                  + ` · ${c.guests} guest(s)`
                  + (c.openBookings ? ` · ${c.openBookings} vendor booking(s)${c.awaitingBookings ? `, ${c.awaitingBookings} awaiting the vendor` : ''}` : '')
                  + (c.stagesPending ? ` · ${c.stagesPending} with vendor stages pending (vouchers/photos)` : ''),
                right: (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {c.isAccountManager && (
                      <Chip size="small" color="secondary" icon={<SupportAgentIcon />}
                        label={c.managedUntil ? `Account manager until ${fmtDate(c.managedUntil)}` : 'Account manager'} />
                    )}
                    <Chip size="small" variant="outlined" label={c.roleLabel} />
                    {family?.id === c.id
                      ? <Chip size="small" color="primary" label="Managing" />
                      : <Button size="small" variant="outlined" onClick={() => manage(c)}>Manage</Button>}
                  </Stack>
                ),
              }))}
              empty="No clients yet. Create a Service Wizard wishlist for a family — you become their account manager and they appear here."
            />
          </Section>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Section title="Needs your attention"><AttentionList items={attention} /></Section>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Section title="Upcoming meetings" action={<LinkButton to="/dashboard/meetings">All meetings</LinkButton>}>
            <RowList
              rows={data.upcomingMeetings.map(m => ({ primary: m.title, secondary: `${fmtDateTime(m.proposed_at)}${m.location ? ` · ${m.location}` : ''}` }))}
              empty="No meetings scheduled."
            />
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Profiles by city"><BarList data={data.profilesByCity.map(c => ({ label: c.label, value: c.count }))} /></Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Profiles by community"><BarList data={data.profilesByCommunity.map(c => ({ label: c.label, value: c.count }))} /></Section>
        </Grid>
        <Grid item xs={12}>
          <Section title="Newest profiles" action={<LinkButton to="/dashboard/matchmaking">Open matchmaking</LinkButton>}>
            <RowList
              rows={data.recentProfiles.map(p => ({
                primary: `${p.name}${p.age ? `, ${p.age}` : ''}`,
                secondary: [p.profession, p.city, p.community].filter(Boolean).join(' · '),
                right: <>
                  <Chip size="small" variant="outlined" label={p.role} sx={{ mr: 1 }} />
                  <Chip size="small" label={fmtDate(p.created_at)} />
                </>,
              }))}
            />
          </Section>
        </Grid>
      </Grid>
    </>
  );
}
